import { useState, useCallback, useEffect } from "react";
import employeeTypeService from "../services/employeeTypeService";
import { SetAuditTrail, SetAuditTrailBulk } from "../../components/Reports/AuditTrailReportEmployeeSetup";

// ** carefull on using showGeneralMessage here since this is not being passed on other module that uses this function.
export const useEmployeeType = ({
  user,
  showGeneralMessage,
  isMainModule = true
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [activeData, setActiveData] = useState([]);
  const [filterData, setFilterData] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await employeeTypeService.getAll();
      setData(res.data || []);
      setFilterData(res.data || []);
    } catch (err) {
      showGeneralMessage?.("Error fetching data!", false);
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isMainModule) return;
    fetchData();
  }, [fetchData, isMainModule]);

  const fetchActiveData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await employeeTypeService.getAllActive();
      setActiveData(res.data || []);
    } catch (err) {
      showGeneralMessage?.("Error fetching data!", false);
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleFilter = (value) => {
    const lowercaseValue = value.toLowerCase();
    if (!value) {
      setData(filterData);
      return;
    }
    const res = filterData.filter((f) => {
      const match1 = f.description?.toString().toLowerCase().includes(lowercaseValue);
      const match2 = f.id?.toString().startsWith(lowercaseValue);
      const match3 = f.code?.toString().toLowerCase().includes(lowercaseValue);
      const match4 = f.isOperationBenefitEligible ? "eligible" : "not eligible";
      const match5 = f.isPartialSalaryEligible ? "eligible" : "not eligible";
      const match6 = f.allowFixedLogs ? "allow fixed logs" : "no fixed logs";
      const match7 = f.maintenanceSalaryCombinationDisregardOtherFilter ? "allow other filter" : "no other filter";
      return match1 || match2 || match3 || match4.includes(lowercaseValue) || match5.includes(lowercaseValue) || match6.includes(lowercaseValue) || match7.includes(lowercaseValue);
    });
    setData(res);
  };

  const createEmployeeType = async (form) => {
    setLoading(true);
    try {
      await employeeTypeService.create(form);
      await SetAuditTrail({
        setupArea: "Employee Type",
        actionTaken: "insert",
        field: "Add",
        newValue: form.description,
        oldValue: "-",
        dateSaved: new Date(),
        savedById: user.id
      });
      showGeneralMessage?.("Employee type created successfully!", true);
      await fetchData();
      return { success: true };
    } catch (error) {
      const msg = error.response?.data?.message || "Failed to create employee type";
      showGeneralMessage?.(msg, false);
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const updateEmployeeType = async (id, form, selectedData) => {
    setLoading(true);
    try {
      await employeeTypeService.update(id, form);

      let auditInfo = [];
      const track = (field, label, isBool = false) => {
        if (form[field] !== selectedData[field]) {
          auditInfo.push({
            setupArea: "Employee Type",
            actionTaken: "update",
            field: label,
            newValue: isBool ? (form[field] ? "Yes/Eligible/Active" : "No/Not Eligible/Inactive") : `${form[field]}`,
            oldValue: isBool ? (selectedData[field] ? "Yes/Eligible/Active" : "No/Not Eligible/Inactive") : `${selectedData[field]}`,
            dateSaved: new Date(),
            savedById: user.id,
          });
        }
      };

      track("code", "Code");
      track("description", "Description");
      track("isOperationBenefitEligible", "Operation Benefit Eligibility", true);
      track("isPartialSalaryEligible", "Partial Salary Eligibility", true);
      track("allowFixedLogs", "Allow Fixed Logs", true);
      track("isActive", "Is Active?", true);
      track("leaveIncentiveFixDays", "Leave Incentive Fix Days");
      track("leaveIncentiveAllowedAbsents", "Leave Incentive Allowed Absents");
      track("maintenanceSalaryCombinationDisregardOtherFilter", "Maintenance Salary Combination Allow Other Filter", true);

      if (auditInfo.length > 0) await SetAuditTrailBulk({ data: auditInfo });

      showGeneralMessage?.("Employee type updated successfully!", true);
      await fetchData();
      return { success: true };
    } catch (error) {
      const msg = error.response?.data?.message || "Failed to update employee type";
      showGeneralMessage?.(msg, false);
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    data,
    activeData,
    handleFilter,
    createEmployeeType,
    updateEmployeeType,
    fetchData,
    fetchActiveData
  };
};